/**
 * Reusable multi-select checkbox dropdown.
 *
 * Used for all audit filter dropdowns (Rooftop, Condition, Flag type).
 * Shows a button with the label and count of selected items.
 * On click, opens a popover with checkboxes for each option.
 * Users can check/uncheck multiple items; apply is implicit (state updates
 * immediately on each checkbox toggle).
 *
 * Accessibility:
 * - Escape closes the dropdown
 * - Click outside closes the dropdown
 */
import React, { useEffect, useRef, useState } from "react"

import { THEME } from "../../config/theme"

interface Props {
  label: string
  options: string[]
  selected: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
  align?: "left" | "right"
}

export default function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  disabled = false,
  align = "left",
}: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open])

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const clearThis = () => {
    onChange([])
  }

  const hasSelection = selected.length > 0
  const buttonLabel = hasSelection ? `${label} (${selected.length})` : label

  const buttonStyle: React.CSSProperties = {
    padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
    fontSize: 13,
    fontWeight: hasSelection ? 500 : 400,
    borderRadius: THEME.borderRadius.md,
    border: `1px solid ${
      hasSelection ? THEME.colors.navy : THEME.colors.silver
    }`,
    backgroundColor: hasSelection
      ? "rgba(13, 26, 66, 0.08)"
      : THEME.colors.neutral.light.surface,
    color: disabled
      ? THEME.colors.neutral.light.textSecondary
      : hasSelection
      ? THEME.colors.navy
      : THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    cursor: disabled ? "not-allowed" : "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  }

  const menuStyle: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    ...(align === "right" ? { right: 0 } : { left: 0 }),
    minWidth: 220,
    maxHeight: 320,
    overflowY: "auto",
    backgroundColor: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    zIndex: 50,
  }

  const itemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
    fontSize: 13,
    color: THEME.colors.neutral.light.textPrimary,
    cursor: "pointer",
    gap: THEME.spacing.sm,
    borderBottom: `1px solid ${THEME.colors.neutral.light.background}`,
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        style={buttonStyle}
      >
        {buttonLabel} ▾
      </button>

      {open && (
        <div style={menuStyle} role="listbox">
          {options.length === 0 && (
            <div
              style={{
                padding: THEME.spacing.md,
                color: THEME.colors.neutral.light.textSecondary,
                fontSize: 13,
              }}
            >
              No options available
            </div>
          )}
          {options.map((opt) => {
            const isSelected = selected.includes(opt)
            return (
              <label
                key={opt}
                style={itemStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    THEME.colors.neutral.light.background
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent"
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(opt)}
                  style={{ cursor: "pointer" }}
                />
                <span>{opt}</span>
              </label>
            )
          })}
          {hasSelection && (
            <div
              style={{
                padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
                borderTop: `1px solid ${THEME.colors.silver}`,
                backgroundColor: THEME.colors.neutral.light.background,
              }}
            >
              <button
                type="button"
                onClick={clearThis}
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: THEME.colors.navy,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: THEME.fonts.primary,
                }}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
